// --------------------------------------------------------
// REGISTER UNIT FORM - RegisterUnitForm
// --------------------------------------------------------

// --------------------------------------------------------
// GENERAL
// --------------------------------------------------------
import { Form } from 'formik'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import Colors from '../../constants/Colors';
import { Paths } from '../../enums/Paths';
import { checkObjectEquality, isEmptyObject } from '../../helpers/HelperFunctions';
import useResponsiveInfo from '../../hooks/useResponsiveInfo';
import { Container, FooterButtonArea, RowContainer } from '../../pages/company/companyRegister/CompanyRegister.styled';
import GenericModal from '../genericModal/GenericModal';
import { GenericSolidButton } from '../UI/buttons/Button.styled';
import CancelOrEditButton from '../UI/buttons/CancelOrEditButton';
import { H1, H2, TextLink } from '../UI/generalLayout/Layout.styled'
import ScreenHeader from '../UI/screenHeader/ScreenHeader';
import BackButton from '../UI/buttons/BackButton';
import { FormContainer } from './Form.styled'
import FormikTextField from './FormikTextField';
import RightIcon from './RightIcon';
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
// --------------------------------------------------------

// --------------------------------------------------------
// REGISTER UNIT FORM INTERFACE
// --------------------------------------------------------

interface IRegisterUnitForm {
    isConfirmationState: boolean;
    formValues: any;
    formErrors: any;
    isEditState: boolean;
    initialValues: any;
    isViewState: boolean;
    error: any;
    onClickCancel: () => void;
    onClickConfirmar: () => void;
    onCloseError: ()=>void;
}
/**
/**
 * A component that renders a Component description here.
 *
 * @param props The component props.
 * @returns The component JSX.
 */
const RegisterUnitForm = ({ isConfirmationState, error, onCloseError, formValues, initialValues, isEditState, isViewState, formErrors, onClickCancel, onClickConfirmar }: IRegisterUnitForm) => {
    // --------------------------------------------------------
    // REGISTER UNIT FORM PRIVATE DECLARATIONS
    // --------------------------------------------------------
    // --------------------------------------------------------
    const confirmButtonLabel = isConfirmationState ? 'Finalizar cadastro' : 'Confirmar cadastro'
    const { isMobile } = useResponsiveInfo()
    const [showModalVoltar, setShowModalVoltar] = useState(false)
    const [noFieldEditedOnEditState, setNoFieldEditedOnEditState] = useState(isEditState)
    const navigator = useNavigate()
    const screenLabel = isViewState
    ? "Visualizar Unidade"
    : isEditState
    ? "Editar Unidade"
    : "Cadastro de Unidade";

    const navigateToUnitList = () => {
        return navigator(Paths.UnitList)
    }

    const onVoltar = () => {
        if (isEmptyObject(formValues) || noFieldEditedOnEditState) {
            navigateToUnitList()
        } else {
            setShowModalVoltar(true)
        }
    }

    const onCancelOrEdit = () => {
        if (isEmptyObject(formValues) || noFieldEditedOnEditState) {
            navigateToUnitList()
        } else {
            onClickCancel()
        }
    }

    useEffect(() => {
        if (isEditState) {
            if (!checkObjectEquality(initialValues, formValues)) {
                setNoFieldEditedOnEditState(false);
            }
        }
    }, [formValues])

    // --------------------------------------------------------
    // REACT RETURN FUNCTION
    // --------------------------------------------------------
    return (
        <>
            <Form>
                <BackButton text="Voltar para listagem de unidades" onClick={onVoltar} />
                <FormContainer isConfirmationState={isConfirmationState}>
                    <ScreenHeader
                        hasRightButton={false}
                        screenTitle={screenLabel}
                        rightButtonText={""}
                        onRightButtonClick={() => { }}
                        errors={error}
                        onCloseError={onCloseError}
                    />
                    <Container>
                        <FormikTextField
                            variant="standard"
                            label="Nome da unidade de atendimento"
                            name="nomeFantasia"
                            disabled={isConfirmationState || isViewState}
                            error={formErrors.nomeFantasia !== undefined}
                            helperText={formErrors.nomeFantasia !== undefined ? formErrors.nomeFantasia : ''}
                            inputProps={{ maxLength: 150 }}
                            InputProps={{
                                endAdornment: <RightIcon value={formValues.nomeFantasia} hasError={formErrors.nomeFantasia} />,
                            }}
                            sx={{ width: isMobile ? '100%' : '49%' }}
                        />
                    </Container>
                    <Container>
                        <FormikTextField
                            variant="standard"
                            name="cnpj"
                            label="CNPJ"
                            mask="99.999.999/9999-99"
                            error={formErrors.cnpj !== undefined}
                            helperText={formErrors.cnpj !== undefined ? formErrors.cnpj : ''}
                            disabled={isConfirmationState || isViewState}
                            InputProps={{
                                endAdornment: <RightIcon value={formValues.cnpj} hasError={formErrors.cnpj} />,
                            }}
                            sx={{ width: isMobile ? '100%' : '49%' }}
                        />
                    </Container>
                    <div style={{ marginTop: 24 }}>
                        <H2>Endereço da empresa</H2>
                        <Container>
                            <FormikTextField
                                variant="standard"
                                name="cep"
                                label="CEP"
                                mask="99.999-999"
                                error={formErrors.cep !== undefined}
                                helperText={formErrors.cep !== undefined ? formErrors.cep : ''}
                                disabled={isConfirmationState || isViewState}
                                InputProps={{
                                    endAdornment: <RightIcon value={formValues.cep} hasError={formErrors.cep} />,
                                }}
                                sx={{ width: isMobile ? '100%' : '49%' }}
                            />
                        </Container>
                        <RowContainer>
                            <FormikTextField
                                variant="standard"
                                label="Endereço"
                                name="logradouro"
                                disabled={isConfirmationState || isViewState}
                                error={formErrors.logradouro !== undefined}
                                helperText={formErrors.logradouro !== undefined ? formErrors.logradouro : ''}
                                inputProps={{ maxLength: 150 }}
                                InputProps={{
                                    endAdornment: <RightIcon value={formValues.logradouro} hasError={formErrors.logradouro} />,
                                }}
                                sx={{ width: isMobile ? '100%' : '49%', marginRight: isMobile ? 0 : 2 }}
                            />
                            <FormikTextField
                                variant="standard"
                                label="Número"
                                name="numero"
                                disabled={isConfirmationState || isViewState}
                                error={formErrors.numero !== undefined}
                                helperText={formErrors.numero !== undefined ? formErrors.numero : ''}
                                inputProps={{ maxLength: 20 }}
                                InputProps={{
                                    endAdornment: <RightIcon value={formValues.numero} hasError={formErrors.numero} />,
                                }}
                                sx={{ width: isMobile ? '100%' : '49%' }}
                            />
                        </RowContainer>
                        <RowContainer>
                            <FormikTextField
                                variant="standard"
                                label="Complemento"
                                name="complemento"
                                disabled={isConfirmationState || isViewState}
                                error={formErrors.complemento !== undefined}
                                helperText={formErrors.complemento !== undefined ? formErrors.complemento : ''}
                                inputProps={{ maxLength: 50 }}
                                InputProps={{
                                    endAdornment: <RightIcon value={formValues.complemento} hasError={formErrors.complemento} />,
                                }}
                                sx={{ width: isMobile ? '100%' : '49%', marginRight: 2 }}
                            />
                            <FormikTextField
                                variant="standard"
                                label="Bairro"
                                name="bairro"
                                disabled={isConfirmationState || isViewState}
                                error={formErrors.bairro !== undefined}
                                helperText={formErrors.bairro !== undefined ? formErrors.bairro : ''}
                                inputProps={{ maxLength: 20 }}
                                InputProps={{
                                    endAdornment: <RightIcon value={formValues.bairro} hasError={formErrors.bairro} />,
                                }}
                                sx={{ width: isMobile ? '100%' : '49%' }}
                            />
                        </RowContainer>
                        <RowContainer>
                            <FormikTextField
                                variant="standard"
                                label="Estado"
                                name="estado"
                                disabled={isConfirmationState || isViewState}
                                error={formErrors.estado !== undefined}
                                helperText={formErrors.estado !== undefined ? formErrors.estado : ''}
                                inputProps={{ maxLength: 2 }}
                                InputProps={{
                                    endAdornment: <RightIcon value={formValues.estado} hasError={formErrors.estado} />,
                                }}
                                sx={{ width: isMobile ? '100%' : '49%', marginRight: 2 }}
                            />
                            <FormikTextField
                                variant="standard"
                                label="Cidade"
                                name="cidade"
                                disabled={isConfirmationState || isViewState}
                                error={formErrors.cidade !== undefined}
                                helperText={formErrors.cidade !== undefined ? formErrors.cidade : ''}
                                inputProps={{ maxLength: 200 }}
                                InputProps={{
                                    endAdornment: <RightIcon value={formValues.cidade} hasError={formErrors.cidade} />,
                                }}
                                sx={{ width: isMobile ? '100%' : '49%' }}
                            />
                        </RowContainer>
                    </div>
                    <div style={{ marginTop: 24 }}>
                        <H2>Contato da Unidade</H2>
                        <RowContainer>
                            <FormikTextField
                                variant="standard"
                                name="telefone1"
                                label="Telefone da unidade"
                                mask="(99) 99999-9999"
                                error={formErrors.telefone1 !== undefined}
                                helperText={formErrors.telefone1 !== undefined ? formErrors.telefone1 : ''}
                                disabled={isConfirmationState || isViewState}
                                InputProps={{
                                    endAdornment: <RightIcon value={formValues.telefone1} hasError={formErrors.telefone1} />,
                                }}
                                sx={{ width: isMobile ? '100%' : '49%', marginRight: 2 }}
                            />
                            {/* <FormikTextField
                                variant="standard"
                                name="telefone2"
                                label="Telefone da unidade (2)"
                                mask="(99) 99999-9999"
                                error={formErrors.telefone2 !== undefined}
                                helperText={formErrors.telefone2 !== undefined ? formErrors.telefone2 : ''}
                                disabled={isConfirmationState || isViewState}
                                InputProps={{
                                    endAdornment: <RightIcon value={formValues.telefone2} hasError={formErrors.telefone2} />,
                                }}
                                sx={{ width: isMobile ? '100%' : '49%' }}
                            /> */}
                        </RowContainer>
                    </div>
                </FormContainer>
                <FooterButtonArea>
                    {!isViewState && (
                        <><CancelOrEditButton
                            isEdit={isConfirmationState}
                            style={{ marginBottom: isMobile ? 12 : 0 }}
                            onEditClick={onCancelOrEdit}
                            onCancelClick={onCancelOrEdit}
                        />
                            <GenericSolidButton
                                type="submit"
                                color={Colors.SPM600}
                                onClick={onClickConfirmar}
                                disabled={noFieldEditedOnEditState}
                            >
                                {confirmButtonLabel}
                            </GenericSolidButton></>)}
                </FooterButtonArea>
            </Form>

            <GenericModal
                modalTitle={'Tem certeza que deseja voltar para a listagem de unidades?'}
                modalSubtitle={'Todos os dados preenchidos serão perdidos'}
                cancelButtonMsg={'Continuar'}
                confirmButtonMsg={'Voltar'}
                visible={showModalVoltar}
                onCancel={navigateToUnitList}
                onConfirm={() => { setShowModalVoltar(false) }}
            />

        </>
    )
    // --------------------------------------------------------
}

export default RegisterUnitForm